import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

interface User {
  _id: string;
  id: string;
  email: string;
  name: string;
  role: string;
  isArtist: boolean;
  googleId?: string;
  profileImage?: string;
  profileImagePosition?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  setToken: (token: string) => Promise<void>;
  getToken: () => Promise<string | null>;
  googleSignIn: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setTokenState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = async (authToken: string) => {
    try {
      console.log('AuthContext: Fetching user with token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/user`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      
      const userData = response.data;
      console.log('AuthContext: User data fetched:', userData);
      
      // Validate user data has required fields
      if (!userData || !userData._id || !userData.email) {
        console.error('AuthContext: Invalid user data received');
        throw new Error('Invalid user data received');
      }

      // Validate role field exists and is valid
      const validRoles = ['superadmin', 'admin', 'gm', 'domain_user', 'guest', 'artist', 'team', 'judge'];
      if (!userData.role || !validRoles.includes(userData.role)) {
        console.error('AuthContext: Invalid or missing role in user data:', userData.role);
        throw new Error('Invalid user role');
      }

      console.log('AuthContext: User role validated:', userData.role);
      setUser(userData);
      setLoading(false); // Set loading to false after successful fetch
    } catch (error: any) {
      console.error('AuthContext: Failed to fetch user:', error.response?.data || error.message);
      setLoading(false); // Set loading to false on error
      logout();
      if (error.response?.status === 401) {
        const currentPath = window.location.pathname;
        localStorage.setItem('redirectPath', currentPath);
        window.location.href = `/login`;
      }
    }
  };
  
  const setToken = async (newToken: string) => {
    setLoading(true);
    console.log('AuthContext: Setting token');
    
    try {
      // Decode and validate the token
      const decoded = JSON.parse(atob(newToken.split('.')[1]));
      console.log('AuthContext: Decoded token:', decoded);
      
      if (!decoded.id || !decoded.role) {
        throw new Error('Invalid token payload');
      }
      
      // Store the token in localStorage
      localStorage.setItem('token', newToken);
      setTokenState(newToken);
      
      // Fetch the user data
      await fetchUser(newToken);
    } catch (error) {
      console.error('AuthContext: Error setting token:', error);
      setLoading(false);
      toast.error('Error setting authentication token');
      logout();
    }
  };

  const getToken = async (): Promise<string | null> => {
    console.log('AuthContext: Getting token');
    
    if (token) {
      return token;
    }
  
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setTokenState(storedToken);
      return storedToken;
    }
  
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/refresh-token`,
        {},
        { withCredentials: true }
      );
      const newToken = response.data.token;
      await setToken(newToken);
      return newToken;
    } catch (error) {
      console.error('AuthContext: Failed to refresh token:', error);
      setLoading(false);
      logout();
      return null;
    }
  };

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/login`, { email, password });
      if (response.data.token) {
        await setToken(response.data.token);
      } else {
        throw new Error('No token received');
      }
    } catch (error: any) {
      console.error('AuthContext: Login failed:', error.response?.data || error.message);
      setLoading(false);
      toast.error(error.response?.data?.message || 'Login failed');
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setTokenState(null);
    setUser(null);
    setLoading(false); // Ensure loading is false after logout
  };

  const googleSignIn = async () => {
    try {
      const currentPath = window.location.pathname;
      window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/google?redirectTo=${encodeURIComponent(currentPath)}`;
    } catch (error) {
      console.error('AuthContext: Google sign-in failed:', error);
      toast.error('Google sign-in failed');
      throw error;
    }
  };

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('AuthContext: Initializing auth state');
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        console.log('AuthContext: Found stored token, fetching user');
        await fetchUser(storedToken);
      } else {
        console.log('AuthContext: No stored token found');
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Debug logging for state changes
  useEffect(() => {
    console.log('AuthContext state update:', {
      hasUser: !!user,
      userRole: user?.role,
      hasToken: !!token,
      isLoading: loading
    });
  }, [user, token, loading]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated: !!user && !!user.role,
      loading,
      login, 
      logout, 
      setToken,
      getToken,
      googleSignIn
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};